import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarBlank, Bed, EnvelopeSimple, SignOut, ArrowSquareOut, WhatsappLogo, LockSimple, Trash, Megaphone, Ticket, Star, CalendarDots } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { OccupancyCalendar } from "@/components/admin/OccupancyCalendar";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { CalendarsTab } from "@/components/admin/CalendarsTab";

const STATUS_STYLES = {
  confirmed: "bg-[#4A6B53]/25 text-[#9DC4A8]",
  pending_payment: "bg-[#A88B5D]/25 text-[#D5C0A1]",
  cancelled: "bg-[#2A2D35] text-[#A0A3A9]",
  payment_failed: "bg-[#8E4A32]/25 text-[#E08A6C]",
};
const STATUS_LABELS = {
  confirmed: "Confirmada",
  pending_payment: "Pendiente",
  cancelled: "Cancelada",
  payment_failed: "Pago fallido",
};

const BookingsTab = () => {
  const [bookings, setBookings] = useState(null);
  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/cancel`);
      toast.success("Reserva cancelada. Las fechas quedan libres.");
      load();
    } catch (e) {
      toast.error("No se pudo cancelar");
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/mark-paid`);
      toast.success("Pago confirmado. Las fechas quedan bloqueadas en firme.");
      load();
    } catch (e) {
      toast.error("No se pudo confirmar el pago");
    }
  };

  const depositAction = async (id, action) => {
    if (action === "capture" && !window.confirm("¿Cobrar la fianza? Hazlo SOLO si hay desperfectos.")) return;
    try {
      await api.post(`/admin/bookings/${id}/deposit/${action}`);
      toast.success(action === "release" ? "Fianza liberada (no se ha cobrado nada)" : "Fianza cobrada");
      load();
    } catch (e) {
      toast.error("No se pudo procesar la fianza");
    }
  };

  if (!bookings) return <p className="text-[#A0A3A9] text-sm p-6">Cargando reservas…</p>;

  return (
    <div data-testid="bookings-tab" className="bg-[#181A1F] border border-[#2A2D35] overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] border-b border-[#2A2D35]">
            <th className="px-5 py-3">Huésped</th>
            <th className="px-5 py-3">Apartamento</th>
            <th className="px-5 py-3">Fechas</th>
            <th className="px-5 py-3">Noches</th>
            <th className="px-5 py-3">Total</th>
            <th className="px-5 py-3">Estado</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {bookings.length === 0 && (
            <tr><td colSpan={7} className="px-5 py-10 text-center text-[#A0A3A9]">Aún no hay reservas.</td></tr>
          )}
          {bookings.map((b) => (
            <tr key={b.id} data-testid={`booking-row-${b.id.slice(0, 8)}`} className="border-b border-[#2A2D35]/60 text-[#FDFBF7]/85">
              <td className="px-5 py-3.5">
                <p>{b.guest_name}</p>
                <p className="text-xs text-[#A0A3A9]">{b.guest_email}</p>
                <p className="text-[10px] text-[#A0A3A9]/80 mt-0.5">
                  {[(b.guests ? `${b.guests} huésp.` : null), (b.pets ? `${b.pets} perro(s)` : null), (b.cribs ? `${b.cribs} cuna(s)` : null)].filter(Boolean).join(" · ")}
                </p>
              </td>
              <td className="px-5 py-3.5">{b.apartment_name}</td>
              <td className="px-5 py-3.5 whitespace-nowrap">{b.checkin} → {b.checkout}</td>
              <td className="px-5 py-3.5">{b.nights}</td>
              <td className="px-5 py-3.5 whitespace-nowrap">
                <p>{b.total_charged != null ? `${b.total_charged.toFixed(2)} €` : `${b.total?.toFixed(2)} €`}</p>
                <p className="text-[10px] text-[#A0A3A9] uppercase tracking-[0.1em]">
                  {{ card: "Tarjeta", paypal: "PayPal", bizum: "Bizum", transfer: "Transferencia" }[b.pay_method] || "Tarjeta"}
                </p>
              </td>
              <td className="px-5 py-3.5">
                <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] ${STATUS_STYLES[b.status] || ""}`}>
                  {STATUS_LABELS[b.status] || b.status}
                </span>
                {b.status === "confirmed" && (
                  <span className="block mt-1.5 text-[10px] text-[#A0A3A9]">
                    {b.checkin_data ? `Check-in ✓ (${b.checkin_data.occupants?.length || 0} ocup.)` : "Check-in pendiente"}
                    {" · Fianza: "}{b.deposit_status || "pendiente"}
                  </span>
                )}
              </td>
              <td className="px-5 py-3.5 text-right whitespace-nowrap">
                {b.deposit_status === "autorizada" && (
                  <>
                    <button data-testid={`deposit-release-${b.id.slice(0, 8)}`} onClick={() => depositAction(b.id, "release")} className="text-xs text-[#9DC4A8] hover:underline mr-3">
                      Liberar fianza
                    </button>
                    <button data-testid={`deposit-capture-${b.id.slice(0, 8)}`} onClick={() => depositAction(b.id, "capture")} className="text-xs text-[#E08A6C] hover:underline mr-3">
                      Cobrar fianza
                    </button>
                  </>
                )}
                {b.status === "pending_payment" && b.pay_method && b.pay_method !== "card" && (
                  <button data-testid={`mark-paid-${b.id.slice(0, 8)}`} onClick={() => markPaid(b.id)} className="text-xs text-[#9DC4A8] hover:underline mr-3">
                    Confirmar pago
                  </button>
                )}
                {b.status === "confirmed" && (
                  <button data-testid={`cancel-booking-${b.id.slice(0, 8)}`} onClick={() => cancel(b.id)} className="text-xs text-[#E08A6C] hover:underline">
                    Cancelar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ContactsTab = () => {
  const [contacts, setContacts] = useState(null);
  const load = () => api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/admin/contacts/${id}/read`).catch(() => {});
    load();
  };

  if (!contacts) return <p className="text-[#A0A3A9] text-sm p-6">Cargando mensajes…</p>;

  return (
    <div data-testid="contacts-tab" className="flex flex-col gap-3">
      {contacts.length === 0 && <p className="text-[#A0A3A9] text-sm p-6 bg-[#181A1F] border border-[#2A2D35]">Aún no hay mensajes.</p>}
      {contacts.map((c) => (
        <div key={c.id} data-testid={`contact-card-${c.id.slice(0, 8)}`} className={`bg-[#181A1F] border p-5 ${c.read ? "border-[#2A2D35] opacity-60" : "border-[#D5C0A1]/40"}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[#FDFBF7] text-sm font-medium">{c.name} <span className="text-[#A0A3A9] font-normal">· {c.email}{c.phone ? ` · ${c.phone}` : ""}</span></p>
              <p className="text-[#A0A3A9] text-sm mt-2 whitespace-pre-line">{c.message}</p>
              <p className="text-[#A0A3A9]/50 text-[10px] uppercase tracking-[0.2em] mt-3">{new Date(c.created_at).toLocaleString("es-ES")}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              {c.phone && (
                <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="p-2 border border-[#2A2D35] text-[#25D366] hover:border-[#25D366] transition-colors" aria-label="WhatsApp">
                  <WhatsappLogo size={16} />
                </a>
              )}
              {!c.read && (
                <button data-testid={`mark-read-${c.id.slice(0, 8)}`} onClick={() => markRead(c.id)} className="px-3 py-2 border border-[#2A2D35] text-[10px] uppercase tracking-[0.15em] text-[#A0A3A9] hover:text-[#FDFBF7] hover:border-[#D5C0A1] transition-colors">
                  Leído
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const BlocksTab = () => {
  const [blocks, setBlocks] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [form, setForm] = useState({ apartment_slug: "", checkin: "", checkout: "" });
  const load = () => api.get("/admin/blocks").then((r) => setBlocks(r.data)).catch(() => setBlocks([]));
  useEffect(() => {
    load();
    api.get("/apartments").then((r) => {
      setApartments(r.data);
      if (r.data.length) setForm((f) => ({ ...f, apartment_slug: f.apartment_slug || r.data[0].slug }));
    }).catch(() => {});
  }, []);

  const add = async () => {
    try {
      await api.post("/admin/blocks", form);
      toast.success("Fechas bloqueadas");
      setForm((f) => ({ ...f, checkin: "", checkout: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo bloquear");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blocks/${id}`);
      toast.success("Bloqueo eliminado. Las fechas quedan libres.");
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  if (!blocks) return <p className="text-[#A0A3A9] text-sm p-6">Cargando bloqueos…</p>;

  return (
    <div data-testid="blocks-tab">
      <div className="bg-[#181A1F] border border-[#2A2D35] p-5 flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Apartamento</label>
          <select
            data-testid="block-apartment"
            value={form.apartment_slug}
            onChange={(e) => setForm({ ...form, apartment_slug: e.target.value })}
            className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5 min-w-[200px]"
          >
            {apartments.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Entrada</label>
          <input data-testid="block-checkin" type="date" value={form.checkin} onChange={(e) => setForm({ ...form, checkin: e.target.value })} className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Salida</label>
          <input data-testid="block-checkout" type="date" value={form.checkout} onChange={(e) => setForm({ ...form, checkout: e.target.value })} className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5" />
        </div>
        <button
          data-testid="block-add"
          onClick={add}
          disabled={!form.checkin || !form.checkout}
          className="px-5 py-2.5 bg-[#D5C0A1] text-[#0F1115] text-xs uppercase tracking-[0.15em] hover:bg-[#FDFBF7] transition-colors disabled:opacity-40"
        >
          Bloquear fechas
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {blocks.length === 0 && <p className="text-[#A0A3A9] text-sm p-6 bg-[#181A1F] border border-[#2A2D35]">No hay fechas bloqueadas manualmente.</p>}
        {blocks.map((b) => (
          <div key={b.id} data-testid={`block-row-${b.id.slice(0, 8)}`} className="bg-[#181A1F] border border-[#2A2D35] p-4 flex items-center justify-between gap-4">
            <div className="text-sm text-[#FDFBF7]/85">
              <span className="font-medium">{b.apartment_name || b.apartment_slug}</span>
              <span className="text-[#A0A3A9] ml-3">{b.checkin} → {b.checkout}</span>
            </div>
            <button data-testid={`block-delete-${b.id.slice(0, 8)}`} onClick={() => remove(b.id)} className="p-2 border border-[#2A2D35] text-[#E08A6C] hover:border-[#E08A6C] transition-colors" aria-label="Eliminar bloqueo">
              <Trash size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TABS = [
  { id: "calendar", label: "Calendario", icon: CalendarBlank },
  { id: "bookings", label: "Reservas", icon: Bed },
  { id: "blocks", label: "Bloqueos", icon: LockSimple },
  { id: "contacts", label: "Mensajes", icon: EnvelopeSimple },
  { id: "marketing", label: "Marketing", icon: Megaphone },
  { id: "reviews", label: "Reseñas", icon: Star },
  { id: "calendars", label: "Calendarios", icon: CalendarDots },
];

const MarketingTab = () => {
  const [guests, setGuests] = useState(null);
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", pct: 10, note: "" });
  const [campaign, setCampaign] = useState({ code: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/admin/guests").then((r) => setGuests(r.data)).catch(() => setGuests([]));
    api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createCode = async () => {
    try {
      await api.post("/admin/codes", { code: form.code || null, pct: Number(form.pct) || 10, note: form.note });
      toast.success("Código creado");
      setForm({ code: "", pct: 10, note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el código");
    }
  };

  const toggleCode = async (code) => {
    await api.post(`/admin/codes/${code}/toggle`).catch(() => {});
    load();
  };

  const sendCampaign = async () => {
    if (!campaign.code) return toast.error("Elige un código");
    if (!window.confirm(`¿Enviar la oferta con el código ${campaign.code.toUpperCase()} a TODOS los huéspedes?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/marketing/send", campaign);
      toast.success(`Campaña enviada a ${data.sent} huéspedes`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar");
    }
    setSending(false);
  };

  if (!guests) return <p className="text-[#A0A3A9] text-sm p-6">Cargando…</p>;

  return (
    <div data-testid="marketing-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-[#181A1F] border border-[#2A2D35] p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-4">Huéspedes ({guests.length})</p>
        <div className="flex flex-col divide-y divide-[#2A2D35] max-h-[420px] overflow-y-auto">
          {guests.length === 0 && <p className="text-sm text-[#A0A3A9] py-6">Aún no hay huéspedes con estancias confirmadas.</p>}
          {guests.map((g) => (
            <div key={g.email} data-testid={`guest-${g.email}`} className="py-3 text-sm text-[#FDFBF7]/85">
              <p>{g.name} <span className="text-xs text-[#A0A3A9]">· {g.stays} estancia(s) · {g.spent.toFixed(2)} €</span></p>
              <p className="text-xs text-[#A0A3A9]">{g.email}{g.phone ? ` · ${g.phone}` : ""}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="bg-[#181A1F] border border-[#2A2D35] p-6">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-4 flex items-center gap-2"><Ticket size={14} /> Códigos de descuento</p>
          <div className="flex gap-2 mb-4">
            <input data-testid="new-code" className="bg-[#0F1115] border border-[#2A2D35] px-3 py-2 text-sm text-[#FDFBF7] flex-1" placeholder="Código (vacío = automático)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            <input data-testid="new-code-pct" type="number" min="1" max="90" className="bg-[#0F1115] border border-[#2A2D35] px-3 py-2 text-sm text-[#FDFBF7] w-20" value={form.pct} onChange={(e) => setForm({ ...form, pct: e.target.value })} />
            <button data-testid="create-code-btn" onClick={createCode} className="px-4 py-2 bg-[#D5C0A1] text-[#0F1115] text-xs uppercase tracking-[0.15em]">Crear</button>
          </div>
          <div className="flex flex-col divide-y divide-[#2A2D35] max-h-[240px] overflow-y-auto">
            {codes.map((c) => (
              <div key={c.code} data-testid={`code-${c.code}`} className="py-2.5 flex items-center justify-between text-sm">
                <span className="text-[#FDFBF7]/85">{c.code} <span className="text-xs text-[#A0A3A9]">−{c.pct}% · {c.source}{c.note ? ` · ${c.note}` : ""} · usado {c.used_count || 0}×</span></span>
                <button data-testid={`toggle-code-${c.code}`} onClick={() => toggleCode(c.code)} className={`text-xs hover:underline ${c.active ? "text-[#9DC4A8]" : "text-[#E08A6C]"}`}>
                  {c.active ? "Activo" : "Inactivo"}
                </button>
              </div>
            ))}
            {codes.length === 0 && <p className="text-sm text-[#A0A3A9] py-4">Sin códigos todavía.</p>}
          </div>
        </div>

        <div className="bg-[#181A1F] border border-[#2A2D35] p-6">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-4">Enviar oferta a todos los huéspedes</p>
          <select data-testid="campaign-code" className="w-full bg-[#0F1115] border border-[#2A2D35] px-3 py-2 text-sm text-[#FDFBF7] mb-3" value={campaign.code} onChange={(e) => setCampaign({ ...campaign, code: e.target.value })}>
            <option value="">Elige el código de la oferta…</option>
            {codes.filter((c) => c.active).map((c) => <option key={c.code} value={c.code}>{c.code} (−{c.pct}%)</option>)}
          </select>
          <textarea data-testid="campaign-message" className="w-full bg-[#0F1115] border border-[#2A2D35] px-3 py-2 text-sm text-[#FDFBF7] mb-3" rows={3} placeholder="Mensaje de la campaña (opcional)…" value={campaign.message} onChange={(e) => setCampaign({ ...campaign, message: e.target.value })} />
          <button data-testid="send-campaign-btn" onClick={sendCampaign} disabled={sending} className="w-full py-3 bg-[#D5C0A1] text-[#0F1115] text-xs uppercase tracking-[0.15em] disabled:opacity-60">
            {sending ? "Enviando…" : `Enviar a ${guests.length} huéspedes`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("calendar");

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F1115]">
      <header className="border-b border-[#2A2D35] bg-[#181A1F]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-serif text-xl text-[#FDFBF7]">Luz de <span className="italic text-[#D5C0A1]">Cádiz</span> <span className="text-[10px] uppercase tracking-[0.25em] text-[#A0A3A9] ml-3">Panel</span></p>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] hover:text-[#FDFBF7] transition-colors">
              <ArrowSquareOut size={14} /> Ver web
            </Link>
            <span className="text-xs text-[#A0A3A9]">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#E08A6C] hover:text-[#FDFBF7] transition-colors">
              <SignOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors ${
                tab === id
                  ? "bg-[#D5C0A1] text-[#0F1115] border-[#D5C0A1]"
                  : "border-[#2A2D35] text-[#A0A3A9] hover:text-[#FDFBF7] hover:border-[#D5C0A1]/50"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {tab === "calendar" && <OccupancyCalendar />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "blocks" && <BlocksTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "marketing" && <MarketingTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "calendars" && <CalendarsTab />}
      </div>
    </div>
  );
}
